import React, { ReactNode, useCallback, useEffect, useState } from 'react';
import { notification } from 'antd';

import ServicesContext from '@totem/components/administration/services/ServicesContext';
import {
  ServiceDefinition,
  ServiceDefinitions,
} from '@totem/components/administration/services/types';
import { getToken } from '@totem/components/userContext/accountUtilities';
import { CONTRACTS_ENDPOINT } from '@totem/utilities/endpoints';

const SERVICES_ENDPOINT = `${CONTRACTS_ENDPOINT}/admin/services`;

const EMPTY_SERVICE: ServiceDefinition = {
  id: '',
  name: '',
  displayName: '',
  description: '',
  aliases: [],
  includedServices: [],
  commissioning: {
    requiresDevice: false,
    requiresBuilding: false,
    toolKeys: [],
    toolApiSiteTypes: [],
  },
  deployment: { createTicket: false, category: '', subCategory: '' },
  decommission: { createTicket: false, category: '', subCategory: '' },
};

type Props = {
  children: ReactNode;
};

const ServicesContainer = ({ children }: Props) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<ServiceDefinitions>([]);
  const [editingService, setEditingService] =
    useState<ServiceDefinition | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const refresh = useCallback(() => {
    setLoading(true);

    fetch(SERVICES_ENDPOINT, {
      headers: { Authorization: `Bearer ${getToken()}` },
    })
      .then((res) => res.json())
      .then((records: ServiceDefinitions) => setData(records ?? []))
      .catch(() => notification.error({ message: 'Failed to load services.' }))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const onAdd = () => {
    setEditingService({ ...EMPTY_SERVICE });

    setModalOpen(true);
  };

  const onEdit = (service: ServiceDefinition) => {
    setEditingService({ ...service });

    setModalOpen(true);
  };

  const onModalClose = () => {
    setModalOpen(false);

    setEditingService(null);
  };

  const onSave = (service: ServiceDefinition) => {
    const isNew = !service.id;
    const url = isNew
      ? SERVICES_ENDPOINT
      : `${SERVICES_ENDPOINT}/${service.id}`;
    const method = isNew ? 'POST' : 'PUT';

    fetch(url, {
      method,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getToken()}`,
      },
      body: JSON.stringify(service),
    })
      .then((res) => {
        if (!res.ok) throw new Error('Save failed');
        notification.success({
          message: `Service ${isNew ? 'created' : 'updated'}.`,
        });

        onModalClose();

        refresh();
      })
      .catch(() => notification.error({ message: 'Failed to save service.' }));
  };

  const onDelete = (id: string) => {
    fetch(`${SERVICES_ENDPOINT}/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${getToken()}` },
    })
      .then((res) => {
        if (!res.ok) throw new Error('Delete failed');
        notification.success({ message: 'Service deleted.' });

        refresh();
      })
      .catch(() =>
        notification.error({ message: 'Failed to delete service.' }),
      );
  };

  return (
    <ServicesContext.Provider
      value={{
        loading,
        data,
        editingService,
        modalOpen,
        onRefresh: refresh,
        onAdd,
        onEdit,
        onModalClose,
        onSave,
        onDelete,
      }}
    >
      {children}
    </ServicesContext.Provider>
  );
};

export default ServicesContainer;
